import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import {
  Alert,
  AlertTitle,
} from "@material-ui/lab";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import { useParams } from "react-router-dom";

import {
  IPaymentIntentResponse,
  IPaymentLinkContext,
  PaymentMethodType,
  PaymentModel,
} from "../../models/PaymentModel";

declare global {
  interface Window {
    Stripe?: any;
  }
}

const loadStripeScript = async () => {
  if (window.Stripe) return window.Stripe;

  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[src="https://js.stripe.com/v3/"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load Stripe.js")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Stripe.js"));
    document.body.appendChild(script);
  });

  return window.Stripe;
};

export const PaymentView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [context, setContext] = useState<IPaymentLinkContext | null>(null);
  const [intent, setIntent] = useState<IPaymentIntentResponse | null>(null);
  const [method, setMethod] = useState<PaymentMethodType>("card");
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const stripeRef = useRef<any>(null);
  const elementsRef = useRef<any>(null);
  const paymentElementRef = useRef<any>(null);
  const paymentContainerRef = useRef<HTMLDivElement | null>(null);

  const isTerminal = useMemo(
    () => context?.order.paymentStatus === "succeeded",
    [context]
  );
  const isProcessing = useMemo(
    () => context?.order.paymentStatus === "processing",
    [context]
  );

  // Reads token-bound context; this is the server source of truth for current payment status.
  const fetchContext = async () => {
    try {
      const nextContext = await PaymentModel.getContext(token);
      setContext(nextContext);
      setError(null);
      return nextContext;
    } catch (nextError: any) {
      setError(nextError.message || "Unable to load payment link");
      return null;
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchContext().finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    return () => {
      // Stripe elements must be cleaned up on route unmount to avoid duplicate mounts.
      if (paymentElementRef.current) {
        paymentElementRef.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    if (
      context?.order.paymentStatus === "succeeded" ||
      context?.order.paymentStatus === "failed"
    ) {
      setNotice(null);
    }
  }, [context?.order.paymentStatus]);

  // Mounts Stripe Payment Element using the returned client secret from server intent creation.
  const mountPaymentElement = async (nextIntent: IPaymentIntentResponse) => {
    if (!paymentContainerRef.current) return;
    const StripeConstructor = await loadStripeScript();
    stripeRef.current = StripeConstructor(nextIntent.publishableKey);
    elementsRef.current = stripeRef.current.elements({
      clientSecret: nextIntent.clientSecret,
    });

    if (paymentElementRef.current) {
      paymentElementRef.current.destroy();
    }

    paymentElementRef.current = elementsRef.current.create("payment");
    paymentElementRef.current.mount(paymentContainerRef.current);
  };

  // Creates/reuses PaymentIntent for the currently selected method and prepares the embedded form.
  const preparePayment = async () => {
    try {
      setPreparing(true);
      setError(null);
      const nextIntent = await PaymentModel.createIntent(token, method);
      setIntent(nextIntent);
      await mountPaymentElement(nextIntent);
      setNotice("Payment form ready.");
    } catch (nextError: any) {
      setError(nextError.message || "Unable to prepare payment form");
    } finally {
      setPreparing(false);
    }
  };

  // Polls backend status because webhook updates happen asynchronously after Stripe confirmation.
  const pollForStatus = async (paymentMethodType: PaymentMethodType) => {
    // ACH can remain in processing longer than card, so keep polling longer before showing a waiting state.
    const maxAttempts = paymentMethodType === "ach" ? 20 : 8;
    const delayMs = paymentMethodType === "ach" ? 3000 : 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const nextContext = await fetchContext();
      if (!nextContext) return;
      if (
        nextContext.order.paymentStatus === "succeeded" ||
        nextContext.order.paymentStatus === "failed"
      ) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  };

  // Confirms payment in browser, then waits for webhook-backed state to settle on the server.
  const submitPayment = async () => {
    if (!stripeRef.current || !elementsRef.current) {
      setError("Prepare the payment form first.");
      return;
    }

    try {
      setPaying(true);
      setError(null);
      setNotice("Submitting payment...");
      const result = await stripeRef.current.confirmPayment({
        elements: elementsRef.current,
        redirect: "if_required",
      });

      if (result.error) {
        setError(result.error.message || "Payment failed");
        setNotice(null);
        return;
      }

      setNotice(
        method === "ach"
          ? "ACH payment submitted. Bank verification can take longer. Confirming status..."
          : "Payment submitted. Confirming status..."
      );
      await pollForStatus(method);

      if (method === "ach") {
        const latestContext = await fetchContext();
        if (latestContext?.order.paymentStatus === "processing") {
          setNotice(
            "ACH payment is still processing. Refresh status shortly to check for final confirmation."
          );
        }
      }
    } catch (nextError: any) {
      setError(nextError.message || "Payment submission failed");
      setNotice(null);
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" style={{ marginTop: "3rem", marginBottom: "3rem" }}>
      <Paper style={{ padding: "2rem" }}>
        <Typography variant="h4" gutterBottom>
          Pay Invoice
        </Typography>
        {error && (
          <Alert severity="error" style={{ marginBottom: "1rem" }}>
            <AlertTitle>Payment unavailable</AlertTitle>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="info" style={{ marginBottom: "1rem" }}>
            {notice}
          </Alert>
        )}
        {context && (
          <>
            <Typography variant="body1">
              <strong>Customer:</strong> {context.order.customerName || "Customer"}
            </Typography>
            <Typography variant="body1">
              <strong>Service:</strong> {context.order.service || "Delivery"}
            </Typography>
            {context.order.quickbooksInvoiceId && (
              <Typography variant="body1">
                <strong>Invoice #:</strong> {context.order.quickbooksInvoiceId}
              </Typography>
            )}
            <Typography variant="body1">
              <strong>Delivery:</strong>{" "}
              {moment(context.order.deliveryAt).format("ddd, MMMM Do YYYY")}
            </Typography>
            <Typography variant="body1">
              <strong>Address:</strong>{" "}
              {[context.order.address, context.order.city, context.order.zipCode]
                .filter(Boolean)
                .join(", ")}
            </Typography>
            <Typography variant="body1">
              <strong>Link expires:</strong>{" "}
              {moment(context.expiresAt).format("ddd, MMMM Do YYYY h:mma")}
            </Typography>
            <Typography variant="body1" style={{ marginTop: "1rem" }}>
              <strong>Status:</strong> {context.order.paymentStatus?.toUpperCase() || "PENDING"}
            </Typography>
            <Typography variant="h6" style={{ marginTop: "1rem" }}>
              Base amount: ${Number(context.order.baseAmount || 0).toFixed(2)}
            </Typography>

            {isTerminal ? (
              <Alert severity="success" style={{ marginTop: "1rem" }}>
                Payment received{context.order.paidAt ? ` on ${moment(context.order.paidAt).format("MMM D, YYYY h:mma")}` : ""}.
              </Alert>
            ) : (
              <>
                <Box mt={2}>
                  <Typography variant="subtitle1">Payment method</Typography>
                  <RadioGroup
                    value={method}
                    onChange={(event) => setMethod(event.target.value as PaymentMethodType)}
                    row
                  >
                    <FormControlLabel value="card" control={<Radio color="primary" />} label="Card" />
                    <FormControlLabel value="ach" control={<Radio color="primary" />} label="ACH" />
                  </RadioGroup>
                </Box>

                <Box mt={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={preparePayment}
                    disabled={preparing || paying}
                  >
                    {preparing ? "Preparing..." : "Load Payment Form"}
                  </Button>
                </Box>

	                {intent && (
	                  <>
	                    <Box mt={2}>
                      {/* Fee and total are server-calculated so UI matches Stripe amount exactly. */}
                      <Typography variant="body2">
                        Fee: ${intent.feeAmount.toFixed(2)}
                      </Typography>
                      <Typography variant="h6">
                        Total: ${intent.totalAmount.toFixed(2)}
                      </Typography>
                    </Box>

                    <div
                      ref={paymentContainerRef}
                      style={{
                        marginTop: "1rem",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        padding: "1rem",
                        minHeight: "72px",
                      }}
                    />
	                    {isProcessing && (
	                      <Alert severity="info" style={{ marginTop: "1rem" }}>
	                        <AlertTitle>ACH processing</AlertTitle>
	                        This payment is still processing with the bank. It can take longer than a
	                        card payment.
	                      </Alert>
	                    )}

	                    <Box mt={2}>
	                      <Button
	                        variant="text"
	                        color="primary"
	                        onClick={() => fetchContext()}
	                        disabled={loading || preparing || paying}
	                      >
	                        Refresh status
	                      </Button>
	                    </Box>
	
	                    <Box mt={2}>
	                      <Button
	                        variant="contained"
	                        color="primary"
                        onClick={submitPayment}
                        disabled={paying}
                      >
                        {paying ? "Submitting..." : `Pay $${intent.totalAmount.toFixed(2)}`}
                      </Button>
                    </Box>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};
